import { useAuth } from "@/hooks/useAuth";
import { useHomeActivity } from "@/hooks/useHomeActivity";
import { useMeetingsQuery } from "@/hooks/useMeetingsQuery";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import {
  Calendar,
  Clock,
  FileText,
  FilePlus,
  FolderPlus,
  Pencil,
  Trash2,
  Users,
  Video,
} from "lucide-react";

const CHANGE_TYPE_CONFIG: Record<
  string,
  { icon: typeof FileText; label: string; color: string }
> = {
  created: { icon: FilePlus, label: "New", color: "var(--success)" },
  modified: { icon: Pencil, label: "Edited", color: "var(--primary)" },
  deleted: { icon: Trash2, label: "Deleted", color: "var(--error)" },
};

function formatRelativeTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return "just now";
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

function formatShortDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatMeetingTime(dateStr: string) {
  const date = new Date(dateStr);
  return (
    date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }) +
    " at " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  );
}

/** Extract first name or short display name from "First Last" or email */
function shortName(name: string | null | undefined): string {
  if (!name) return "";
  if (name.includes("@")) return name.split("@")[0];
  return name.split(" ")[0];
}

export default function HomeView() {
  const { backendToken } = useAuth();
  const navigate = useNavigate();

  const tokenForQuery = backendToken || undefined;

  const { data: deltaCards = [], isFetching: loadingActivity } =
    useHomeActivity();

  const { data: meetings = [], isFetching: loadingMeetings } =
    useMeetingsQuery("upcoming", tokenForQuery, { daysAhead: 14 });

  const upcomingMeetings = meetings.slice(0, 7);

  const handleViewChange = (view: string) => {
    const routes: Record<string, string> = {
      library: "/dashboard/library",
      "project-creation": "/dashboard/new-project",
    };
    const route = routes[view];
    if (route) navigate({ to: route });
  };

  const goToUpcomingMeetings = useCallback(
    () => navigate({ to: "/dashboard/meetings", search: { tab: "upcoming" as const } }),
    [navigate],
  );

  return (
    <div className="p-6 space-y-8">
      {/* Engagements / Delta Cards Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2
              className="text-2xl font-bold"
              style={{ color: "var(--text-primary)" }}
            >
              Engagements
            </h2>
            {!loadingActivity && deltaCards.length > 0 && (
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: "var(--surface)",
                  color: "var(--text-secondary)",
                }}
              >
                {deltaCards.length}
              </span>
            )}
          </div>
          <button
            onClick={() => handleViewChange("project-creation")}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-colors"
            style={{
              backgroundColor: "var(--accent)",
              color: "var(--primary-foreground)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--accent-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--accent)";
            }}
          >
            <FolderPlus className="w-4 h-4" />
            New Project
          </button>
        </div>

        {loadingActivity ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="rounded-lg p-5 animate-pulse"
                style={{ backgroundColor: "var(--surface)" }}
              >
                <div
                  className="h-4 rounded w-2/3 mb-4"
                  style={{ backgroundColor: "var(--bg)" }}
                />
                <div
                  className="h-3 rounded w-full mb-2"
                  style={{ backgroundColor: "var(--bg)" }}
                />
                <div
                  className="h-3 rounded w-4/5"
                  style={{ backgroundColor: "var(--bg)" }}
                />
              </div>
            ))}
          </div>
        ) : deltaCards.length === 0 ? (
          <div
            className="rounded-lg p-8 text-center"
            style={{
              backgroundColor: "var(--surface)",
              border: "1px dashed var(--border)",
            }}
          >
            <FileText
              className="w-8 h-8 mx-auto mb-3"
              style={{ color: "var(--text-secondary)" }}
            />
            <p
              className="text-sm mb-3"
              style={{ color: "var(--text-secondary)" }}
            >
              No project activity yet
            </p>
            <button
              onClick={() => handleViewChange("project-creation")}
              className="text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: "var(--accent)", color: "white" }}
            >
              Create Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {deltaCards.map((card) => (
              <button
                key={card.project_id}
                onClick={() =>
                  navigate({
                    to: "/dashboard/projects/$projectId",
                    params: { projectId: card.project_id },
                  })
                }
                className="rounded-lg text-left transition-all group"
                style={{
                  backgroundColor: "var(--surface)",
                  border: "1px solid var(--border)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
              >
                {/* Card header */}
                <div
                  className="flex items-center justify-between px-4 pt-4 pb-3"
                  style={{
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <h3
                    className="text-sm font-semibold truncate mr-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {card.project_name}
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {card.needs_agent_refresh && (
                      <span
                        className="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor:
                            "color-mix(in srgb, var(--accent) 12%, transparent)",
                          color: "var(--accent)",
                        }}
                      >
                        <Clock className="w-3 h-3" />
                        Queued
                      </span>
                    )}
                    {card.meeting_count > 0 && (
                      <span
                        className="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: "var(--secondary)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        <Video className="w-3 h-3" />
                        {card.meeting_count}
                      </span>
                    )}
                  </div>
                </div>

                {/* File activity rows */}
                <div className="px-4 py-2">
                  {card.recent_files && card.recent_files.length > 0 ? (
                    <div className="space-y-0">
                      {card.recent_files.map((file, idx) => {
                        const cfg =
                          CHANGE_TYPE_CONFIG[file.change_type || "modified"] ||
                          CHANGE_TYPE_CONFIG.modified;
                        const ChangeIcon = cfg.icon;
                        return (
                          <div
                            key={`${file.file_name}-${idx}`}
                            className="flex items-center gap-2 py-1.5 min-w-0"
                            style={{
                              borderBottom:
                                idx < card.recent_files.length - 1
                                  ? "1px solid color-mix(in srgb, var(--border) 50%, transparent)"
                                  : "none",
                            }}
                          >
                            <ChangeIcon
                              className="w-3.5 h-3.5 flex-shrink-0"
                              style={{ color: cfg.color }}
                            />
                            <span
                              className="text-xs font-medium truncate"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {file.file_name}
                            </span>
                            <span
                              className="text-[11px] ml-auto flex-shrink-0 flex items-center gap-1.5"
                              style={{ color: "var(--text-secondary)" }}
                            >
                              {file.modified_by && (
                                <span className="truncate max-w-[80px]">
                                  {shortName(file.modified_by)}
                                </span>
                              )}
                              {file.modified_at && (
                                <span
                                  style={{
                                    fontVariantNumeric: "tabular-nums",
                                    opacity: 0.7,
                                  }}
                                >
                                  {formatShortDate(file.modified_at)}
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {card.file_change_count > 3 && (
                        <div
                          className="text-[11px] pt-1"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          +{card.file_change_count - 3} more files
                        </div>
                      )}
                    </div>
                  ) : card.recent_file_names?.length > 0 ? (
                    /* Fallback: name-only rows for backward compat */
                    <div className="space-y-0">
                      {card.recent_file_names.slice(0, 3).map((name, idx) => (
                        <div
                          key={`${name}-${idx}`}
                          className="flex items-center gap-2 py-1.5"
                          style={{
                            borderBottom:
                              idx <
                              Math.min(card.recent_file_names.length, 3) - 1
                                ? "1px solid color-mix(in srgb, var(--border) 50%, transparent)"
                                : "none",
                          }}
                        >
                          <FileText
                            className="w-3.5 h-3.5 flex-shrink-0"
                            style={{ color: "var(--text-secondary)" }}
                          />
                          <span
                            className="text-xs font-medium truncate"
                            style={{ color: "var(--text-primary)" }}
                          >
                            {name}
                          </span>
                        </div>
                      ))}
                      {card.file_change_count > 3 && (
                        <div
                          className="text-[11px] pt-1"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          +{card.file_change_count - 3} more files
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="py-2 text-xs"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      No recent file activity
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  className="flex items-center gap-3 px-4 py-2.5 text-[11px]"
                  style={{
                    color: "var(--text-secondary)",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  {card.file_change_count > 0 && (
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span
                        className="font-medium"
                        style={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {card.file_change_count}
                      </span>{" "}
                      files tracked
                    </span>
                  )}
                  {card.last_graph_sync && (
                    <span className="flex items-center gap-1 ml-auto">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(card.last_graph_sync)}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Upcoming Meetings Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2
              className="text-2xl font-bold"
              style={{ color: "var(--text-primary)" }}
            >
              Upcoming Meetings
            </h2>
          </div>
          {meetings.length > 7 && (
            <button
              onClick={goToUpcomingMeetings}
              className="text-sm transition-colors"
              style={{ color: "var(--text-secondary)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--text-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--text-secondary)";
              }}
            >
              View all
            </button>
          )}
        </div>

        {loadingMeetings ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-lg p-4 animate-pulse"
                style={{ backgroundColor: "var(--surface)" }}
              >
                <div
                  className="h-4 rounded w-1/2 mb-2"
                  style={{ backgroundColor: "var(--bg)" }}
                />
                <div
                  className="h-3 rounded w-1/3"
                  style={{ backgroundColor: "var(--bg)" }}
                />
              </div>
            ))}
          </div>
        ) : upcomingMeetings.length === 0 ? (
          <div
            className="rounded-lg p-6 text-center"
            style={{
              backgroundColor: "var(--surface)",
              border: "1px dashed var(--border)",
            }}
          >
            <Calendar
              className="w-8 h-8 mx-auto mb-3"
              style={{ color: "var(--text-secondary)" }}
            />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              No upcoming meetings
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {upcomingMeetings.map((meeting) => (
              <button
                key={meeting.id}
                onClick={goToUpcomingMeetings}
                className="w-full rounded-lg p-4 text-left transition-colors flex items-center gap-4"
                style={{
                  backgroundColor: "var(--surface)",
                  border: "1px solid var(--border)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
              >
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-sm font-medium truncate"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {meeting.title}
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {formatMeetingTime(meeting.datetime)}
                    {meeting.duration > 0 && ` -- ${meeting.duration} min`}
                  </p>
                </div>
                {meeting.attendees?.length > 0 && (
                  <span
                    className="flex items-center gap-1 text-xs flex-shrink-0"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    <Users className="w-3 h-3" />
                    {meeting.attendees.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
